import { Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDateTime, useAppNotification } from '@feature/utils';

import { useTravelReferenceDocumentDelete } from '../model/TravelReferenceDocumentQuery';
import { TravelReferenceDocumentSlot } from '../model/TravelReferenceDocumentTypes';
import { getTravelReferenceDocumentSlotLabel } from '../travelReferenceDocumentLabels';
import { TravelReferenceDocumentPreviewModal } from './TravelReferenceDocumentPreviewModal';
import { TravelReferenceDocumentSlotActions } from './TravelReferenceDocumentSlotActions';
import { TravelReferenceDocumentUploadModal } from './TravelReferenceDocumentUploadModal';

interface TravelReferenceDocumentSlotRowProps {
  slot: TravelReferenceDocumentSlot;
}

export function TravelReferenceDocumentSlotRow({ slot }: TravelReferenceDocumentSlotRowProps) {
  const { t } = useTranslation();
  const notification = useAppNotification();
  const [uploadOpened, { open: openUpload, close: closeUpload }] = useDisclosure(false);
  const [previewOpened, { open: openPreview, close: closePreview }] = useDisclosure(false);
  const deleteDocument = useTravelReferenceDocumentDelete();
  const label = getTravelReferenceDocumentSlotLabel(t, slot);

  // A preview left open over a slot that emptied would reopen by itself on the next upload into the row.
  useEffect(() => {
    if (!slot.present) closePreview();
  }, [slot.present, closePreview]);

  // Promise handlers still run after an unmount, unlike mutate-level callbacks.
  const onDelete = () =>
    deleteDocument
      .mutateAsync({ kind: slot.kind, role: slot.role })
      .then(notification.success)
      .catch(notification.error);

  return (
    <Group
      gap="xs"
      align="center"
      justify="space-between"
      wrap="nowrap"
    >
      <Stack
        gap={2}
        miw={0}
      >
        <Text
          size="sm"
          c={slot.present ? undefined : 'dimmed'}
          fs={slot.present ? undefined : 'italic'}
        >
          {label}: {slot.present ? slot.fileName : t('travel.referenceDocuments.missing')}
        </Text>
        {slot.present && (
          <Text
            size="xs"
            c="dimmed"
          >
            {t(`travel.referenceDocuments.${slot.uploadedByName == null ? 'uploadedNoName' : 'uploaded'}`, {
              date: formatDateTime(slot.uploadedAt),
              name: slot.uploadedByName,
            })}
          </Text>
        )}
      </Stack>
      <TravelReferenceDocumentSlotActions
        slot={slot}
        label={label}
        onView={openPreview}
        onUpload={openUpload}
        onDelete={onDelete}
        isDeleting={deleteDocument.isPending}
      />
      {uploadOpened && (
        <TravelReferenceDocumentUploadModal
          slot={slot}
          onClose={closeUpload}
        />
      )}
      {previewOpened && slot.present && (
        <TravelReferenceDocumentPreviewModal
          document={slot}
          onClose={closePreview}
        />
      )}
    </Group>
  );
}
